import { message } from "antd";
import { useEffect, useState } from "react";

import {
    useReceiptsForwardAddressGetV2,
    useReceiptsNormalizedCompanyNamePutV2,
} from "@btnet/services-btnet/handlers/receipts/receipts";

import {
    BdsIconCheck,
    BdsIconCopy,
    BdsIconFileDocument,
    BdsIconPaperPlaneRight,
    BdsIconPencilSimple,
    BdsIconSparkle,
    BdsText,
} from "@buildertrend/components";

import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import yup from "utilities/form/yup";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTInput } from "commonComponents/btWrappers/BTInput/BTInput";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import {
    type IBTInjectedFormikProps,
    withFormikBT,
} from "commonComponents/utilities/Formik/WithFormikBT";

import "./EmailModal.less";

interface IEmailModalProps {
    onClose: () => void;
    variant?: "receipts" | "bills";
}

export interface IEmailModalFormValues {
    email: string;
}

const EmailModal: React.FC<IBTInjectedFormikProps<IEmailModalProps, IEmailModalFormValues>> = (
    props,
) => {
    const { onClose, values, setFieldValue, setTouched, setFieldError, validateForm } = props;
    const [isEditModeEnabled, setIsEditModeEnabled] = useState(false);
    const [hasBeenCopied, setHasBeenCopied] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const variant = props.variant ?? "receipts";
    const PAYABLES_DOMAIN = "payables.buildertrend.net";

    const userContext = useRequiredContext(UserInfoContext);

    const { data, refetch, isLoading } = useReceiptsForwardAddressGetV2();

    const displayDomain = variant === "bills" ? PAYABLES_DOMAIN : data?.data.domain;

    const {
        mutateAsync: saveCompanyName,
        error,
        isSuccess,
        isLoading: isSaving,
    } = useReceiptsNormalizedCompanyNamePutV2();

    useEffect(() => {
        if (error) {
            setFieldError("email", error.message);
        }
    }, [error, setFieldError]);

    useEffect(() => {
        if (isSuccess) {
            setIsEditModeEnabled(false);
            void message.success("Email address updated.");
        }
    }, [isSuccess]);

    useEffect(() => {
        setHasBeenCopied(false);
    }, [data?.data.normalizedCompanyName]);

    if (isLoading) {
        return <BTLoading />;
    }

    return (
        <>
            <BTModal
                data-testid="btModalEmailForward"
                visible
                centered
                width="880px"
                removeBodyPadding
                beforeClose={onClose}
                title={
                    variant === "bills"
                        ? "Capture draft bills from emailed costs"
                        : "Email costs to Buildertrend"
                }
                className="EmailModal"
            >
                <BTRow>
                    <BTCol span={24} className="container">
                        <BTRow>
                            {variant === "bills"
                                ? "Share this email address with vendors. We'll automatically extract the details and create draft bills for your review."
                                : "Send bills and receipts directly to your Cost Inbox email - we'll handle the rest."}
                        </BTRow>
                        <BTRow className="panel margin-top-md">
                            <BTCol span={8} className="padding-top-sm padding-bottom-sm">
                                <BTRow align="middle" justify="center">
                                    <BdsIconPaperPlaneRight
                                        size="lg"
                                        className="icon margin-bottom-xs"
                                    />
                                </BTRow>
                                <BTRow align="middle" justify="center">
                                    <BTTitle level={2}>Send</BTTitle>
                                </BTRow>
                                <BTRow align="middle" justify="center">
                                    <BdsText
                                        text="Email your costs to this address"
                                        size="normal-md"
                                    />
                                </BTRow>
                            </BTCol>
                            <BTCol span={8} className="padding-top-sm padding-bottom-sm">
                                <BTRow align="middle" justify="center">
                                    <BdsIconSparkle size="lg" className="icon margin-bottom-xs" />
                                </BTRow>
                                <BTRow align="middle" justify="center">
                                    <BTTitle level={2}>Extract</BTTitle>
                                </BTRow>
                                <BTRow align="middle" justify="center">
                                    <BdsText
                                        text={
                                            variant === "bills"
                                                ? "We'll scan and pull key details"
                                                : "We'll read the invoice or receipt and pull key details"
                                        }
                                        size="normal-md"
                                    />
                                </BTRow>
                            </BTCol>
                            <BTCol span={8} className="padding-top-sm padding-bottom-sm">
                                <BTRow align="middle" justify="center">
                                    <BdsIconFileDocument
                                        size="lg"
                                        className="icon margin-bottom-xs"
                                    />
                                </BTRow>
                                <BTRow align="middle" justify="center">
                                    <BTTitle level={2}>Confirm</BTTitle>
                                </BTRow>
                                <BTRow align="middle" justify="center">
                                    <BdsText
                                        text={
                                            variant === "bills"
                                                ? "You review the draft bill"
                                                : "Review and confirm the details"
                                        }
                                        size="normal-md"
                                    />
                                </BTRow>
                            </BTCol>
                        </BTRow>
                        <BTRow>
                            <BdsText size="distinct-md">
                                {variant === "bills" ? "Bills intake email" : "Forwarding address"}
                            </BdsText>
                        </BTRow>
                        <BTRow className="margin-bottom-sm">
                            <BdsText size="normal-sm">
                                {variant === "bills"
                                    ? "Anything sent to this address will appear under Bills \u2192 Inbox."
                                    : "This email can be used by all members of your team or shared with subs and vendors. Emails sent to any previous forwarding addresses will continue to reach your Cost Inbox."}
                            </BdsText>
                        </BTRow>
                        {isEditModeEnabled ? (
                            <>
                                <BTRow justify="space-between" gutter={8}>
                                    <BTCol flex={1}>
                                        <BTFormItemAutomatic<IEmailModalFormValues> id="email">
                                            <BTInput
                                                id="CostInboxEmailModalEmailInput"
                                                data-testid="CostInboxEmailModalEmailInput"
                                                onChange={(_, value) => {
                                                    setFieldValue("email", value);
                                                    setTouched({ email: true });
                                                }}
                                                value={values.email}
                                                suffix={`@${displayDomain ?? ""}`}
                                                className="CostInboxEmailModalEmailInput"
                                            />
                                        </BTFormItemAutomatic>
                                    </BTCol>
                                    <BTCol>
                                        <BTButton
                                            data-testid="CostInboxEmailModalCancelEdit"
                                            type="secondary"
                                            className="margin-right-xs"
                                            onClick={() => setIsEditModeEnabled(false)}
                                        >
                                            Cancel
                                        </BTButton>
                                        <BTButton
                                            data-testid="CostInboxEmailModalEditSaveEdit"
                                            type="primary"
                                            disabled={!props.touched || !props.isValid}
                                            onClick={async () => {
                                                const errors = await validateForm();
                                                if (Object.keys(errors).length === 0) {
                                                    setIsConfirmOpen(true);
                                                }
                                            }}
                                        >
                                            Save
                                        </BTButton>
                                    </BTCol>
                                </BTRow>
                                <BdsText size="normal-sm">
                                    You can only update your forwarding address once.
                                </BdsText>
                            </>
                        ) : (
                            <BTRow
                                justify="space-between"
                                className="margin-bottom-sm margin-top-sm"
                            >
                                <BTCol>
                                    <BdsText
                                        text={data?.data.normalizedCompanyName}
                                        className="EmailModal-email-address"
                                        size="distinct-md"
                                    />
                                    @
                                    <BdsText
                                        text={displayDomain}
                                        className="EmailModal-email-address"
                                        size="normal-lg"
                                    />
                                </BTCol>
                                <BTCol>
                                    {!data?.data.hasBeenEdited && (
                                        <BTPopover
                                            content={
                                                userContext.roleInfo?.roleName !== "Org owner"
                                                    ? "Only org owners can modify the forwarding address."
                                                    : ""
                                            }
                                            disabled={
                                                userContext.roleInfo?.roleName === "Org owner"
                                            }
                                        >
                                            <BTButton
                                                data-testid="CostInboxEmailModalEdit"
                                                aria-label="Edit"
                                                icon={<BdsIconPencilSimple />}
                                                className="margin-right-xs"
                                                type="secondary"
                                                disabled={
                                                    userContext.roleInfo?.roleName !== "Org owner"
                                                }
                                                onClick={() => setIsEditModeEnabled(true)}
                                            />
                                        </BTPopover>
                                    )}
                                    <BTButton
                                        data-testid="CostInboxEmailModalCopy"
                                        icon={hasBeenCopied ? <BdsIconCheck /> : <BdsIconCopy />}
                                        type="secondary"
                                        onClick={() => {
                                            void navigator.clipboard.writeText(
                                                `${data?.data.normalizedCompanyName ?? ""}@${
                                                    displayDomain ?? ""
                                                }`,
                                            );
                                            setHasBeenCopied(true);
                                        }}
                                    >
                                        {hasBeenCopied ? "Copied" : "Copy"}
                                    </BTButton>
                                </BTCol>
                            </BTRow>
                        )}
                    </BTCol>
                </BTRow>
            </BTModal>
            <BTModal
                visible={isConfirmOpen}
                title="Change forwarding address"
                centered
                width="560px"
                beforeClose={() => setIsConfirmOpen(false)}
                removeBodyPadding
            >
                <BTRow className="padding-right-md padding-left-md padding-bottom-md">
                    <BTCol span={24}>
                        <BTRow>
                            <BTCol>
                                You are about to change your forwarding address. This cannot be
                                undone.
                                <br />
                                Emails sent to any previous forwarding addresses will continue to
                                reach your
                                <br />
                                Cost Inbox.
                            </BTCol>
                        </BTRow>
                        <BTRow className="margin-top-md margin-bottom-lg">
                            <BTCol>
                                <BdsText
                                    text={values.email}
                                    className="EmailModal-email-address"
                                    size="distinct-md"
                                />
                                @
                                <BdsText
                                    text={displayDomain}
                                    className="EmailModal-email-address"
                                    size="normal-lg"
                                />
                            </BTCol>
                        </BTRow>
                        <BTRow gutter={8} justify="end">
                            <BTCol>
                                <BTButton
                                    key="next"
                                    data-testid="cancelConfirmReceiptsEmailUpdate"
                                    type="secondary"
                                    loading={isSaving}
                                    onClick={() => setIsConfirmOpen(false)}
                                >
                                    Cancel
                                </BTButton>
                            </BTCol>
                            <BTCol>
                                <BTButton
                                    key="next"
                                    data-testid="confirmReceiptsEmailUpdate"
                                    type="primary"
                                    loading={isSaving}
                                    onClick={async () => {
                                        try {
                                            await saveCompanyName({
                                                data: { normalizedCompanyName: values.email },
                                            });
                                        } catch (error) {
                                            // Do nothing
                                        }
                                        await refetch();
                                        setIsConfirmOpen(false);
                                    }}
                                >
                                    Confirm
                                </BTButton>
                            </BTCol>
                        </BTRow>
                    </BTCol>
                </BTRow>
            </BTModal>
        </>
    );
};

export default withFormikBT<IEmailModalProps, IEmailModalFormValues>({
    mapPropsToValues: () => ({
        email: "",
    }),
    validationSchema: yup.object({
        email: yup
            .string()
            .required("Enter an email alias.")
            .min(3, "Minimum 3 characters.")
            .max(50, "Maximum 64 characters.")
            .matches(
                /^[a-zA-Z]([a-zA-Z0-9._-]*[a-zA-Z0-9])?$/,
                "Use letters, numbers, and underscores; start with a letter; no ending underscore.",
            )
            .test("no-consecutive-periods", "Cannot have consecutive periods", (value: string) => {
                return value ? !value.includes("..") : true;
            })
            .test("no-consecutive-underscores", "No consecutive underscores.", (value: string) => {
                return value ? !value.includes("__") : true;
            })
            .test("no-at-symbol", "Should not contain @ symbol", (value: string) => {
                return value ? !value.includes("@") : true;
            }),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    handleSubmit: (_values, _formikBag) => {
        // The saveAddress mutation is handled in the Save button's onClick
    },
})(EmailModal);
